import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar, Clock, ArrowRight, ArrowUpRight, Search } from 'lucide-react';
import { Input } from '../components/ui/input';

const blogPosts = [
  {
    id: 1,
    slug: 'assurance-temporaire-quand-souscrire',
    title: "Assurance temporaire : dans quels cas la souscrire ?",
    excerpt: "L'assurance temporaire est idéale pour de nombreuses situations : achat d'un véhicule d'occasion, prêt de voiture, import/export... Découvrez tous les cas d'usage.",
    category: "Auto",
    categoryColor: "bg-blue-100 text-blue-700",
    date: "15 Déc 2025",
    readTime: "5 min",
    image: "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=800&h=500&fit=crop",
    featured: true
  },
  {
    id: 2,
    slug: 'carte-grise-demarches-simplifiees',
    title: "Carte grise : simplifiez vos démarches administratives",
    excerpt: "Depuis la fermeture des guichets en préfecture, les démarches carte grise se font en ligne. Voici comment nous pouvons vous accompagner.",
    category: "Carte Grise",
    categoryColor: "bg-teal-100 text-teal-700",
    date: "10 Déc 2025",
    readTime: "4 min",
    image: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&h=500&fit=crop"
  },
  {
    id: 3,
    slug: 'assurance-habitation-conseils',
    title: "5 conseils pour bien choisir son assurance habitation",
    excerpt: "Propriétaire ou locataire, l'assurance habitation est essentielle. Voici nos conseils pour trouver la couverture adaptée à vos besoins.",
    category: "Habitation",
    categoryColor: "bg-emerald-100 text-emerald-700",
    date: "5 Déc 2025",
    readTime: "6 min",
    image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=500&fit=crop"
  },
  {
    id: 4,
    slug: 'rc-professionnelle-obligatoire',
    title: "RC Professionnelle : est-elle obligatoire pour votre activité ?",
    excerpt: "La responsabilité civile professionnelle protège votre entreprise. Découvrez si elle est obligatoire dans votre secteur d'activité.",
    category: "Pro",
    categoryColor: "bg-amber-100 text-amber-700",
    date: "28 Nov 2025",
    readTime: "5 min",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&h=500&fit=crop"
  },
  {
    id: 5,
    slug: 'mutuelle-sante-bien-choisir',
    title: "Comment bien choisir sa mutuelle santé ?",
    excerpt: "Entre les garanties, les franchises et les délais de carence, il n'est pas toujours facile de s'y retrouver. Nos conseils pour faire le bon choix.",
    category: "Santé",
    categoryColor: "bg-rose-100 text-rose-700",
    date: "20 Nov 2025",
    readTime: "7 min",
    image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=800&h=500&fit=crop"
  },
  {
    id: 6,
    slug: 'sinistre-auto-que-faire',
    title: "Sinistre auto : les étapes à suivre",
    excerpt: "Accident, vol, bris de glace... Que faire en cas de sinistre automobile ? Guide pratique pour bien réagir et être indemnisé rapidement.",
    category: "Auto",
    categoryColor: "bg-blue-100 text-blue-700",
    date: "15 Nov 2025",
    readTime: "5 min",
    image: "https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=800&h=500&fit=crop"
  }
];

const categories = [
  { id: "all", label: "Tous les articles" },
  { id: "auto", label: "Auto" },
  { id: "habitation", label: "Habitation" },
  { id: "sante", label: "Santé" },
  { id: "pro", label: "Pro" },
  { id: "carte-grise", label: "Carte Grise" }
];

export const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === "all" || 
      post.category.toLowerCase().replace(' ', '-') === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredPost = blogPosts.find(p => p.featured);
  const regularPosts = filteredPosts.filter(p => !p.featured || selectedCategory !== "all");

  return (
    <>
      <Helmet>
        <title>Blog Assurance | Conseils CAMBC Conseils Moissac</title>
        <meta name="description" content="Conseils et actualités assurance : auto, habitation, santé, pro. Blog du Cabinet CAMBC Conseils, courtier à Moissac." />
        <link rel="canonical" href="https://www.cambc-conseils.fr/blog" />
      </Helmet>

      <div className="min-h-screen bg-[#F9F8F6]">
        {/* Hero Section */}
        <section className="relative pt-32 pb-16 bg-[#0F2137] overflow-hidden">
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#C5A059] mb-4 block">
                Actualités & Conseils
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6 tracking-tight">
                Le Blog
              </h1>
              <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-8">
                Découvrez nos articles pour mieux comprendre l'assurance et faire les bons choix.
              </p>

              {/* Search Bar */}
              <div className="max-w-md mx-auto relative">
                <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Rechercher un article..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 pr-4 py-6 bg-white/10 border-white/20 text-white placeholder:text-slate-400 rounded-xl focus:bg-white focus:text-[#0F2137] transition-all"
                />
              </div>
            </div>
          </div>
          
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#C5A059] to-transparent"></div>
        </section>

        {/* Categories Filter */}
        <section className="py-8 bg-white border-b border-slate-100 sticky top-20 z-30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 ${
                    selectedCategory === cat.id
                      ? 'bg-[#0F2137] text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Article */}
        {selectedCategory === "all" && featuredPost && !searchQuery && (
          <section className="py-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <Link to={`/blog/${featuredPost.slug}`} className="group block">
                <Card className="bg-white border-0 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-500 overflow-hidden">
                  <CardContent className="p-0">
                    <div className="grid lg:grid-cols-2">
                      <div className="aspect-[16/10] lg:aspect-auto overflow-hidden">
                        <img 
                          src={featuredPost.image} 
                          alt={featuredPost.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                      </div>
                      <div className="p-8 lg:p-12 flex flex-col justify-center">
                        <div className="flex items-center gap-3 mb-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${featuredPost.categoryColor}`}>
                            {featuredPost.category}
                          </span>
                          <span className="text-slate-400 text-sm">Article à la une</span>
                        </div>
                        <h2 className="text-2xl lg:text-3xl font-bold text-[#0F2137] mb-4 group-hover:text-[#C5A059] transition-colors">
                          {featuredPost.title}
                        </h2>
                        <p className="text-slate-600 leading-relaxed mb-6">
                          {featuredPost.excerpt}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <Calendar size={14} />
                              {featuredPost.date}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock size={14} />
                              {featuredPost.readTime}
                            </span>
                          </div>
                          <span className="flex items-center gap-1 text-[#C5A059] font-medium group-hover:gap-2 transition-all">
                            Lire l'article
                            <ArrowRight size={16} />
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </div>
          </section>
        )}

        {/* Articles Grid */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {regularPosts.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {regularPosts.map((post) => (
                  <Link key={post.id} to={`/blog/${post.slug}`} className="group">
                    <Card className="h-full bg-white border-0 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-500 overflow-hidden group-hover:-translate-y-1">
                      <CardContent className="p-0">
                        <div className="aspect-[16/10] overflow-hidden">
                          <img 
                            src={post.image} 
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                          />
                        </div>
                        <div className="p-6">
                          <div className="flex items-center gap-3 mb-3">
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${post.categoryColor}`}>
                              {post.category}
                            </span>
                          </div>
                          <h3 className="text-lg font-bold text-[#0F2137] mb-3 group-hover:text-[#C5A059] transition-colors line-clamp-2">
                            {post.title}
                          </h3>
                          <p className="text-slate-600 text-sm leading-relaxed mb-4 line-clamp-2">
                            {post.excerpt}
                          </p>
                          <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-3 text-slate-500">
                              <span className="flex items-center gap-1">
                                <Calendar size={12} />
                                {post.date}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock size={12} />
                                {post.readTime}
                              </span>
                            </div>
                            <ArrowUpRight size={16} className="text-[#C5A059] opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-slate-500 text-lg mb-4">Aucun article trouvé pour cette recherche.</p>
                <Button 
                  variant="outline"
                  onClick={() => { setSelectedCategory("all"); setSearchQuery(""); }}
                  className="border-[#0F2137] text-[#0F2137]"
                >
                  Voir tous les articles
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Newsletter CTA */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0F2137] mb-4">
              Restez informé
            </h2>
            <p className="text-slate-600 mb-8">
              Des questions sur l'assurance ? Contactez-nous pour un conseil personnalisé.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/quote">
                <Button className="bg-[#C5A059] hover:bg-[#B08A45] text-white px-8 py-6 rounded-xl">
                  Demander un devis
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-2 border-[#0F2137] text-[#0F2137] px-8 py-6 rounded-xl">
                  Nous contacter
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};
